import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { MainLayout } from "../layout/MainLayout";
import { AlertCircle } from "lucide-react";
import Card from "../common/Card";
import SectionHeader from "../common/SectionHeader";
import FileUploadZone from "../uploads/FileUploadZone";
import PrimaryButton from "../ui/PrimaryButton";
import Toast from "../common/Toast";
import JSZip from "jszip";

const ZipExtractor = () => {
  const { projectId } = useParams();

  const apiRoot = process.env.REACT_APP_API_ROOT;
  const [fileData, setFileData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileName, setFileName] = useState("No file chosen");
  const [toast, setToast] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({
    stage: "",
    progress: 0,
  });
  const [extractedFiles, setExtractedFiles] = useState([]);
  const [apiResults, setApiResults] = useState([]);

  const apiRootOperational = process.env.REACT_APP_API_ROOT_OPERATIONAL;
  const token = localStorage.getItem("token");

  const showToast = (message, type = "success") => {
    setToast({ message, type });
  };

  const hideToast = () => {
    setToast(null);
  };

  const handleFilesSelect = (files) => {
    if (files.length === 0) return;
    const file = files[0];

    if (!file.name.endsWith(".zip")) {
      showToast("Please select a ZIP file", "error");
      return;
    }

    setFileName(file.name);
    setFileData(file);
    setExtractedFiles([]);
    setApiResults([]);
  };

  const uploadFileToBlob = async (fileBlob, destPath) => {
    const formData = new FormData();
    formData.append("file", fileBlob);
    formData.append("destPath", destPath);
    formData.append("overwrite", "true");

    const response = await fetch(
      `${apiRootOperational}/api/files/upload-form`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error || `Failed to upload file to ${destPath}`
      );
    }

    return response.json();
  };

  const registerGifData = async (direction, imageFileBlob, filename) => {
    try {
      const formData = new FormData();
      formData.append("project", projectId);
      formData.append("direction", direction);
      formData.append("image_file", imageFileBlob, filename);

      const response = await fetch(`${apiRoot}/api/wind_gif_data/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("API Error Response:", errorData);
        throw new Error(errorData.error || "Failed to register GIF data");
      }

      return response.json();
    } catch (error) {
      console.error(
        `Error registering GIF data for direction ${direction}:`,
        error
      );
      return { error: error.message, direction };
    }
  };

  const extractDirectionFromPath = (filePath) => {
    const filename = filePath.split("/").pop();

    const direction = filename.split(".")[0];

    if (/^\d+$/.test(direction)) {
      return direction;
    }

    const match = filename.match(/(\d+)/);
    if (match) {
      return match[1];
    }

    return null;
  };

  const extractAndUploadZip = async (file) => {
    try {
      setUploadProgress({
        stage: "Reading ZIP file...",
        progress: 10,
      });

      const zip = await JSZip.loadAsync(file);

      const filesToProcess = [];

      zip.forEach((relativePath, zipEntry) => {
        if (
          !zipEntry.dir &&
          !relativePath.startsWith("__MACOSX/") &&
          !relativePath.startsWith(".") &&
          !relativePath.includes("/.") &&
          relativePath.toLowerCase().endsWith(".gif")
        ) {
          filesToProcess.push({
            path: relativePath,
            entry: zipEntry,
          });
        }
      });

      if (filesToProcess.length === 0) {
        throw new Error("No valid GIF files found in the ZIP archive");
      }

      setUploadProgress({
        stage: `Found ${filesToProcess.length} GIF files to extract`,
        progress: 20,
      });

      let commonPrefix = "";

      if (filesToProcess.length > 0) {
        const firstFile = filesToProcess[0].path;
        const firstSlashIndex = firstFile.indexOf("/");

        if (firstSlashIndex > 0) {
          const potentialPrefix = firstFile.substring(0, firstSlashIndex + 1);
          const allHavePrefix = filesToProcess.every((file) =>
            file.path.startsWith(potentialPrefix)
          );

          if (allHavePrefix) {
            commonPrefix = potentialPrefix;
            console.log(`Detected common directory prefix: ${commonPrefix}`);
          }
        }
      }

      const targetBasePath = `master/GIF/${projectId}/`;
      let processedCount = 0;
      const processedFiles = [];
      const apiCallResults = [];

      for (const fileInfo of filesToProcess) {
        let destFilename = fileInfo.path;
        if (commonPrefix && destFilename.startsWith(commonPrefix)) {
          destFilename = destFilename.substring(commonPrefix.length);
        }

        const destPath = targetBasePath + destFilename;
        const fileContent = await fileInfo.entry.async("blob");
        const filename = destFilename.split("/").pop();

        const uploadResult = await uploadFileToBlob(fileContent, destPath);

        const direction = extractDirectionFromPath(destFilename);

        if (direction) {
          const apiResult = await registerGifData(
            direction,
            fileContent,
            filename
          );
          apiCallResults.push({
            direction,
            path: destPath,
            result: apiResult,
          });
        }

        processedCount++;
        processedFiles.push(destPath);

        setUploadProgress({
          stage: `Processing files (${processedCount}/${filesToProcess.length})...`,
          progress:
            20 + Math.floor((processedCount / filesToProcess.length) * 75),
        });
      }

      setUploadProgress({
        stage: "Processing completed successfully",
        progress: 100,
      });

      setExtractedFiles(processedFiles);
      setApiResults(apiCallResults);

      return {
        success: true,
        extractPath: targetBasePath,
        fileCount: processedCount,
        apiCallCount: apiCallResults.length,
      };
    } catch (error) {
      console.error("Error in extraction process:", error);
      throw error;
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!fileData) {
      showToast("Please select a ZIP file", "error");
      return;
    }

    if (!projectId) {
      showToast(
        "No project ID found. Please ensure you're in a project context",
        "error"
      );
      return;
    }

    setIsSubmitting(true);
    showToast("Processing ZIP file...", "info");
    setUploadProgress({ stage: "Starting extraction process...", progress: 0 });

    try {
      const result = await extractAndUploadZip(fileData);

      showToast(
        `Successfully extracted ${result.fileCount} files and registered ${result.apiCallCount} GIF records`,
        "success"
      );

      // Don't reset the file data so user can see the results
      // setFileData(null);
      // setFileName("No file chosen");
    } catch (error) {
      console.error("Error in extraction process:", error);
      showToast(`Extraction failed: ${error.message}`, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Render list of extracted files
   */
  const renderExtractedFiles = () => {
    if (extractedFiles.length === 0) return null;

    return (
      <div className="mt-4 bg-gray-50 p-4 rounded-md border border-gray-200">
        <h3 className="text-sm font-medium text-gray-700 mb-2">
          Extracted Files ({extractedFiles.length})
        </h3>
        <div className="max-h-40 overflow-y-auto">
          <ul className="text-xs text-gray-600 space-y-1">
            {extractedFiles.map((file, index) => (
              <li key={index} className="font-mono">
                {file}
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  /**
   * Render API call results
   */
  const renderApiResults = () => {
    if (apiResults.length === 0) return null;

    return (
      <div className="mt-4 bg-gray-50 p-4 rounded-md border border-gray-200">
        <h3 className="text-sm font-medium text-gray-700 mb-2">
          GIF Registration Results ({apiResults.length})
        </h3>
        <div className="max-h-40 overflow-y-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-1 text-left">Direction</th>
                <th className="p-1 text-left">File</th>
                <th className="p-1 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {apiResults.map((result, index) => (
                <tr
                  key={index}
                  className={
                    result.result.error ? "text-red-600" : "text-green-600"
                  }
                >
                  <td className="p-1">{result.direction}</td>
                  <td className="p-1 font-mono truncate max-w-xs">
                    {result.path.split("/").pop()}
                  </td>
                  <td className="p-1">
                    {result.result.error ? "Error" : "Success"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <MainLayout>
      {/* Toast notification */}
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={hideToast} />
      )}

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card
            title={`Wind GIF Data Uploader - Project ${projectId}`}
            subtitle="Extract GIF files and register them with the system"
          >
            <div className="space-y-6">
              {/* Main Form */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6">
                  <SectionHeader title="Upload and Register GIFs" />

                  <div className="space-y-4">
                    {/* File Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Select ZIP File with GIFs
                      </label>
                      <FileUploadZone
                        onFilesSelect={handleFilesSelect}
                        disabled={isSubmitting}
                        accept=".zip"
                        acceptedTypes={["ZIP"]}
                      />
                      {fileName !== "No file chosen" && (
                        <p className="mt-2 text-sm text-gray-600">
                          Selected file: {fileName}
                        </p>
                      )}
                    </div>

                    {/* Destination Info */}
                    <div className="bg-gray-50 p-3 rounded-md border border-gray-200">
                      <p className="text-sm text-gray-700">
                        Files will be extracted to:{" "}
                        <span className="font-mono font-medium">
                          master/GIF/{projectId}/
                        </span>
                      </p>
                      <p className="text-sm text-gray-700 mt-1">
                        GIF data will be registered with the Wind GIF Data API
                      </p>
                    </div>

                    {/* Progress Indicator */}
                    {uploadProgress.stage && (
                      <div className="mb-4">
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div
                            className="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
                            style={{ width: `${uploadProgress.progress}%` }}
                          ></div>
                        </div>
                        <p className="text-sm text-gray-600 mt-2">
                          {uploadProgress.stage}
                        </p>
                      </div>
                    )}

                    {/* Submit Button */}
                    <PrimaryButton
                      onClick={handleSubmit}
                      isLoading={isSubmitting}
                      disabled={!fileData}
                      loadingText="Processing..."
                    >
                      Upload and Register GIFs
                    </PrimaryButton>

                    {/* API Results */}
                    {renderApiResults()}

                    {/* Extracted Files List */}
                    {renderExtractedFiles()}
                  </div>
                </div>
              </div>

              {/* Info Section */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6">
                  <SectionHeader title="Important Information" />

                  <div className="bg-blue-50 p-4 rounded-md">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <AlertCircle className="h-5 w-5 text-blue-400" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800">
                          About This Tool
                        </h3>
                        <div className="mt-2 text-sm text-blue-700">
                          <ul className="list-disc pl-5 space-y-2">
                            <li>
                              GIF files will be uploaded to
                              master/GIF/[project_id]/
                            </li>
                            <li>
                              Wind direction is extracted from the filename
                              (e.g., "90.gif" has direction "90")
                            </li>
                            <li>
                              Each GIF is automatically registered with the
                              project and its direction
                            </li>
                            <li>
                              Files must have their direction in the filename to
                              be properly registered
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default ZipExtractor;
